import classNames from "classnames";
import {
  Control,
  Controller,
  FieldError,
  RegisterOptions,
  useController,
} from "react-hook-form";
import ReactSelect, { Props as SelectProps } from "react-select";
import CreatableSelect from "react-select/creatable";
import { InputError, errorBorder } from "../InputError";
import { ReactNode } from "react";

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  id: string;
  label: ReactNode;
  className?: string;
  validation?: RegisterOptions;
  creatable?: boolean;
  onCreateOption?: (inputValue: string) => void;
} & SelectProps;

export const getSelectClassNames: (
  error?: FieldError
) => SelectProps["classNames"] = (error) => ({
  control: () =>
    classNames(
      "w-full border !border-secondary-200 !rounded-lg pl-5 pr-5 py-3 text-secondary-800 !bg-transparent",
      error?.message && errorBorder
    ),
  placeholder: () => "!text-secondary-400",
  valueContainer: () => "!p-0 !m-0",
  option: ({ isFocused, isDisabled }) =>
    classNames(
      "!text-secondary-800 hover:!bg-secondary-200",
      isFocused ? "!bg-secondary-100" : "!bg-transparent",
      isDisabled && "opacity-50"
    ),
  input: () => "!m-0 !p-0",
  singleValue: () => "!m-0 !p-0",
  dropdownIndicator: () => "!m-0 !py-0",
  menu: () => "!bg-secondary-50",
  multiValue: () => "!bg-secondary-200 !rounded-md",
  multiValueRemove: () => "!bg-secondary-200 !rounded-md",
});

export const Select = ({
  control,
  id,
  label,
  className,
  validation,
  placeholder,
  noOptionsMessage,
  creatable,
  onCreateOption,
  ...restProps
}: Props) => {
  const {
    fieldState: { error },
  } = useController({ name: id, control });

  return (
    <div className={classNames("grid", "flex-col", className)}>
      <label
        className="text-secondary-600 text-xs font-normal mb-1.5"
        htmlFor={id}
      >
        {label}
        {validation?.required ? "*" : ""}
      </label>
      <Controller
        control={control}
        name={id}
        rules={validation}
        render={({ field }) => {
          const props: SelectProps = {
            ...restProps,
            instanceId: id,
            inputId: id,
            classNames: getSelectClassNames(error),
            onChange: field.onChange,
            value: field.value,
            placeholder: placeholder ?? "Vælg...",
            noOptionsMessage: noOptionsMessage ?? (() => "Ingen muligheder"),
            loadingMessage: () => "Indlæser...",
          };

          if (creatable)
            return (
              <CreatableSelect
                formatCreateLabel={(inputValue) => (
                  <span
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    Klik her til at tilføje butikskategorien:{" "}
                    <span
                      style={{
                        color: "#37311d",
                        fontWeight: "bold",
                      }}
                    >
                      {inputValue}
                    </span>
                  </span>
                )}
                onCreateOption={onCreateOption}
                {...props}
              />
            );

          return <ReactSelect {...props} />;
        }}
      />
      <InputError>{error?.message}</InputError>
    </div>
  );
};
